import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
} from 'firebase/auth';

// Defense logic: check if firebase config is provided
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const isFirebaseConfigured = !!firebaseConfig.apiKey && !!firebaseConfig.projectId;

let app: any;
let auth: any;

if (isFirebaseConfigured) {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  auth = getAuth(app);

  // Use Emulator if specified
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099');
  }
} else {
  console.warn(
    '[Firebase] Settings are missing. Falling back to local Mock Auth Mode.'
  );
}

// ---------------------------------------------------------------------------
// Mock Authentication for Local Development without Firebase credentials
// ---------------------------------------------------------------------------

interface MockUser {
  uid: string;
  email: string;
  displayName: string;
  getIdToken: () => Promise<string>;
}

class MockAuthManager {
  private listeners: ((user: MockUser | null) => void)[] = [];
  private currentUser: MockUser | null = null;

  constructor() {
    const saved = localStorage.getItem('secret_chat_mock_user');
    if (saved) {
      try {
        const u = JSON.parse(saved);
        this.currentUser = this.createMockUser(u.uid, u.email, u.displayName);
      } catch {
        this.currentUser = null;
      }
    }
  }

  private createMockUser(uid: string, email: string, name: string): MockUser {
    return {
      uid,
      email,
      displayName: name,
      getIdToken: async () => `test_${uid}`, // JWT bypass pattern in API
    };
  }

  onAuthStateChanged(callback: (user: MockUser | null) => void) {
    this.listeners.push(callback);
    callback(this.currentUser);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l(this.currentUser));
  }

  async signInWithEmail(email: string): Promise<MockUser> {
    const uid = 'mock_user_' + email.replace(/[^a-zA-Z0-9]/g, '_');
    this.currentUser = this.createMockUser(uid, email, email.split('@')[0]);
    localStorage.setItem('secret_chat_mock_user', JSON.stringify({ uid, email, displayName: this.currentUser.displayName }));
    this.notify();
    return this.currentUser;
  }

  async signUpWithEmail(email: string): Promise<MockUser> {
    return this.signInWithEmail(email);
  }

  async signInWithGoogle(): Promise<MockUser> {
    return this.signInWithEmail('google_user@example.com');
  }

  async signOut() {
    this.currentUser = null;
    localStorage.removeItem('secret_chat_mock_user');
    this.notify();
  }

  getCurrentUser() {
    return this.currentUser;
  }
}

export const mockAuth = new MockAuthManager();

// ---------------------------------------------------------------------------
// Wrappers that seamlessly switch between Real Firebase and Mock Auth
// ---------------------------------------------------------------------------

export const googleProvider = isFirebaseConfigured ? new GoogleAuthProvider() : null;

export function getFirebaseAuth() {
  return isFirebaseConfigured ? auth : null;
}

export { isFirebaseConfigured };

export function listenToAuth(callback: (user: any | null) => void) {
  if (isFirebaseConfigured) {
    return onAuthStateChanged(auth, callback);
  } else {
    return mockAuth.onAuthStateChanged(callback);
  }
}

export async function loginWithEmail(email: string, password: string): Promise<any> {
  if (isFirebaseConfigured) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } else {
    return mockAuth.signInWithEmail(email);
  }
}

export async function registerWithEmail(email: string, password: string): Promise<any> {
  if (isFirebaseConfigured) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } else {
    return mockAuth.signUpWithEmail(email);
  }
}

export async function loginWithGoogle(): Promise<any> {
  if (isFirebaseConfigured && googleProvider) {
    const userCredential = await signInWithPopup(auth, googleProvider);
    return userCredential.user;
  } else {
    return mockAuth.signInWithGoogle();
  }
}

export async function logout(): Promise<void> {
  if (isFirebaseConfigured) {
    await firebaseSignOut(auth);
  } else {
    await mockAuth.signOut();
  }
}
